import { Link } from 'react-router-dom'
import { useAuth } from '../core/auth'
import {
  ROLES, ROLE_TOOLS, ROSTER_ROLES, TOOL_LABELS, CAP_BY_ID, holdsRole, roleLabels,
} from '../core/caps'
import { useUsers } from '../modules/admin/model'
import { ErrorState } from '../ui/States'

/**
 * /roles — what each role is for, per tool, and (for whoever hands out
 * permissions) who holds it.
 *
 * Everybody gets this page. A teacher wondering why they cannot see placement
 * should be able to find out what Office means without asking anyone, and the
 * badge in the top bar links here for exactly that.
 *
 * The capability text comes straight from the catalog in src/core/caps.ts, so
 * this page and the permissions screen can never describe a role differently.
 */
export function Roles() {
  const { profile, can, isSeminarTeacher } = useAuth()
  const mayGrant = can('admin.grant')
  const { rows, err } = useUsers(mayGrant)

  return (
    <>
      <h1 className="page-title" style={{ margin: '0 0 var(--s-1)' }}>Roles &amp; responsibilities</h1>
      <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '0 0 var(--s-4)', lineHeight: 1.5 }}>
        Each tool has its own roles, and they are independent: being Office in NorseTerm says nothing
        about any other tool. {profile?.districtAdmin && 'As a district admin you hold every role.'}
        {mayGrant && <> Change who holds what on the <Link to="/admin/accounts">accounts</Link> screen.</>}
      </p>

      {mayGrant && err && <ErrorState body={`Could not load who holds each role. ${err}`} />}

      {ROLE_TOOLS.map((tool) => (
        <section key={tool} style={{ marginBottom: 'var(--s-5)' }}>
          <h2 className="work__heading">{TOOL_LABELS[tool] ?? tool}</h2>
          <div style={{ display: 'grid', gap: 'var(--s-3)' }}>
            {ROLES.filter((r) => r.tool === tool).map((r) => {
              const mine = !!profile && (profile.districtAdmin || holdsRole(profile.caps, r))
              // By what they are called: an admin is listed under Admin, not
              // under Office too, though Admin includes what Office does.
              const holders = (rows ?? []).filter((u) => u.active !== false && !u.districtAdmin
                && roleLabels(u, r.tool).includes(r.label))
              return (
                <article key={r.id} className="card" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                    <h3 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{r.label}</h3>
                    {mine && <span className="pill pill--good">You</span>}
                  </div>
                  <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', margin: 'var(--s-1) 0 var(--s-2)', lineHeight: 1.5 }}>
                    {r.summary}
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 'var(--s-4)', fontSize: 'var(--t-sm)', color: 'var(--ink-2)', lineHeight: 1.5 }}>
                    {r.caps.map((c) => {
                      const def = CAP_BY_ID.get(c)
                      return def ? <li key={c}><strong>{def.label}.</strong> {def.detail}</li> : null
                    })}
                  </ul>
                  {mayGrant && rows && (
                    <p className="opt__hint" style={{ marginTop: 'var(--s-2)' }}>
                      {holders.length === 0 ? 'Nobody yet.' : <>
                        Held by{' '}
                        {holders.map((u, i) => (
                          <span key={u.id}>
                            {i > 0 && ', '}
                            <Link to={`/admin/accounts?person=${encodeURIComponent(u.id)}`}>{u.displayName}</Link>
                          </span>
                        ))}
                      </>}
                    </p>
                  )}
                </article>
              )
            })}
            {ROSTER_ROLES.filter((r) => r.tool === tool).map((r) => (
              <article key={r.label} className="card" style={{ margin: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
                  <h3 style={{ fontSize: 'var(--t-md)', margin: 0 }}>{r.label}</h3>
                  <span className="pill">From the student list</span>
                  {r.label === 'Seminar teacher' && isSeminarTeacher && <span className="pill pill--good">You</span>}
                </div>
                <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', margin: 'var(--s-1) 0 0', lineHeight: 1.5 }}>
                  {r.summary}
                </p>
              </article>
            ))}
          </div>
        </section>
      ))}
    </>
  )
}
